import clsx from "clsx";
import React, { FC } from "react";

import { Icon } from "../../icon";
import { useEventDetail } from "../event-detail/event-detail-context";
import { useEventDates } from "../use-event-dates";
import { EventHeroBannerProps } from "./event-hero-banner";
import { EventHeroCardInner } from "./event-hero-card-inner";
import { EventHeroSubscribe } from "./event-hero-subscribe";

export interface EventHeroCardProps extends EventHeroBannerProps {
  className?: string;
}

export const EventHeroCard: FC<EventHeroCardProps> = ({ className, onScrollToVideo }) => {
  const { event, labels, subscription } = useEventDetail();

  // This component is not rendered if event is null or undefined, we can safely use the non-null assertion
  const { formattedDateRange, isSubscriptionOpen, isPastEvent } = useEventDates(event!);

  if (!event) return null;

  const { location, country } = event;

  const isSoldOut = event.subscriptionTypes.filter((sub) => !sub.soldOut).length === 0;

  return (
    <div className={clsx("w-full flex flex-col rounded-3xl overflow-hidden shadow-lg", className)}>
      <div className="p-5 bg-white">
        <div className="flex flex-col md:flex-row flex-wrap gap-2 justify-between w-full">
          <div className="flex-none">
            <h3 className="text-3xl lg:text-4xl font-bold">{formattedDateRange.toUpperCase()}</h3>
            {(country || location) && (
              <div className="mt-2 flex gap-2 items-center font-light">
                <Icon icon="map-pin" /> {[country, location].filter(Boolean).join(", ")}
              </div>
            )}
          </div>
          {!isPastEvent && (
            <div className="flex-none md:mt-2">
              {isSoldOut
                ? labels["event-sold-out"]
                : isSubscriptionOpen
                ? labels["event-subscriptions-open"]
                : labels["event-subscriptions-closed"]}
            </div>
          )}
        </div>
        {isPastEvent ? (
          <>
            <div className="mt-4 text-lg lg:text-xl font-medium">
              {event.videoContent
                ? labels["event-past-relive-with-video"]
                : labels["event-past-relive-without-video"]}
            </div>
            {event.videoContent && (
              <button
                type="button"
                onClick={onScrollToVideo}
                className={clsx(
                  "rounded-full w-14 h-14 bg-primary text-white",
                  "inline-flex justify-center items-center mt-4"
                )}
              >
                <Icon icon="long-arrow-down" size={36} />
              </button>
            )}
          </>
        ) : (
          <EventHeroCardInner className="mt-4" />
        )}
      </div>
      {!isPastEvent && (isSubscriptionOpen || subscription) && (
        <div className="p-5 bg-primary text-white">
          <EventHeroSubscribe />
        </div>
      )}
    </div>
  );
};
